import { stageDot, type OutreachStage } from '../../../lib/outreach-stage';
import { STAGE_DOT_LABEL_KEY } from '../../../lib/outreach-card';
import { useTranslation } from '../../../lib/i18n';
import { statusInk } from '../../../lib/task-status';
import styles from './contacts.module.css';

/**
 * An outreach stage as a DOT — the one place a stage becomes a colour, because the contact row, the Inbox
 * row, a thread header and the contact screen's rows all draw it. Replaces the old turn BADGE: the stage
 * says more than three badge words could, so the words moved into the line beside it (`StageLine`).
 *
 * The colour is the Badge Style's own ink (`statusInk`), so a dot can never tell a different story from a
 * badge for the same state. Done draws no dot (`stageDot` → null). One meaning per colour, on all three surfaces:
 * orange = needs you (an open decision), blue = scheduled (a held send), red = stuck (not delivered, or no reply
 * for a while), near-black = in progress (the AI has it). The dot NAMES itself (`outreach.dot.*`) as its
 * accessible name and its hover tooltip — it used to be `aria-hidden` with no legend anywhere (Ronny, 2026-10-22:
 * "a black dot, sometimes a red one, with no explanation").
 * Shared 1:1 with iOS `StageDot.swift` and Android `StageDot.kt`.
 */
export function StageDot({ stage, className }: { stage: OutreachStage | null | undefined; className?: string }) {
  const { t } = useTranslation();
  const dot = stageDot(stage);
  if (!dot || !stage || stage === 'done') return null;
  const label = t(STAGE_DOT_LABEL_KEY[stage]);
  return (
    <span
      className={`${styles.stageDot}${className ? ` ${className}` : ''}`}
      style={{ background: statusInk(dot) }}
      role="img"
      aria-label={label}
      title={label}
    />
  );
}
